import CancelablePromise, {CancelablePromiseType} from 'cancelable-promise';

export interface ApiClientOptions {
  interpretResponse?: boolean
  locale?: string
  clientId?: string
  clientSecret?: string
  host?: string
  apiGwHost?: string
  invalidateCallback?: () => void
  userAgent?: string
  gatekeeperDebug?: boolean
  fetchFn: typeof fetch
  fetchTimeout?: number
  log: (...args: any[]) => void
}

// `fetchFn` is intentionally NOT defaulted: callers must inject one (e.g.
// `(url, init) => enhancedFetch(url, {...init, bot})`) so outbound calls carry a
// TickarooBot identity and never fall back to the unidentified global fetch.
const defaultOptions: Partial<ApiClientOptions> = {
  host: "",
  interpretResponse: true,
  locale: "en",
  log: args => {}
};

export class FatalApiError extends Error {
}

export class ApiResponseError<T = any> extends Error {
  readonly json: T
  readonly status: number

  constructor(status: number, json: T) {
    super("ApiResponseError Status " + status)
    this.json = json;
    this.status = status;
  }
}
export function isApiResponseError(error: Error | ApiResponseError | unknown): error is ApiResponseError {
  // `null`/`undefined` are legal Promise rejection values, and callers reach this from a
  // catch block where the caught value is `unknown` — reading `.status` off them would
  // throw *while handling* the original failure and swallow the caller's error path.
  return error != null && (error as ApiResponseError).status !== undefined
}

function cancelableFetch(fetchFn: typeof fetch, timeout: number|undefined, input: string, init?: RequestInit): CancelablePromiseType<Response> {
  return new CancelablePromise<Response>((resolve, reject, onCancel) => {
    const controller = new AbortController()
    const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : undefined;

    onCancel(() => controller.abort());

    init = init ? ({ ...init, signal: controller.signal }) : { signal: controller.signal }
    fetchFn(input, init).then(resolve).catch(reject).finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    })
  })
}

export class ApiClient {
  static API_V6_SYNC_REPORTER_META_INFOS = "/api/v6/sync/reporter_meta_infos.json";
  static API_V6_SYNC_LOAD_TICKER = "/api/v6/sync/load_ticker.json";
  static API_V6_SYNC_LOAD_EDITOR = "/api/v6/sync/load_editor.json";
  static API_V6_SYNC_POST_MODIFICATIONS = "/api/v6/sync/post_modifications.json";

  static API_V6_TICKER_SHOW = "/api/v6/ticker/show.json";

  static API_SUBSCRIPTIONS_V1_COMPILED_CAPABILITIES = "/api/subscriptions/v1/compiled_capabilities.json";
  static API_SUBSCRIPTIONS_V1_CUSTOMER_STATE = "/api/subscriptions/v1/customer_state.json";
  static API_SUBSCRIPTIONS_V1_ON_DEMAND_STATE = "/api/subscriptions/v1/on_demand_state.json";
  static API_SUBSCRIPTIONS_V1_CREDITS_SPEND_LIVEBLOGE = "/api/subscriptions/v1/credits/spend_liveblog.json";

  // mediaservice migrated to the TS Lambda on API Gateway: served under /apigw/* so the
  // apiGwHost convention routes internal Lambda callers to INTERNAL_APIGW_HOSTNAME and
  // browsers go via the CloudFront /apigw/* behavior. (Old Ruby service was /api/mediaservice.)
  // Only the GET endpoint exists; the POST web_embed_request/web_embed_code endpoints had
  // no consumers and were dropped in the migration (embed codes go via ?url=<raw code>).
  static API_WEB_EMBED = "/apigw/mediaservice/v3/web_embed.json";

  static API_V6_EDITOR_THIRD_PARTY_CONTENT = "/api/v6/editor_third_party_content/index.json";

  static API_COMMENTS_V1_COMMENTS_LIST = "/api/comments/v1/comments/list.json";
  static API_COMMENTS_V1_PUBLIC_CHANNEL_SETTINGS = "/api/comments/v1/owner/:ownerId/public/:channelId/version/:version/settings.json";
  static API_COMMENTS_V1_PUBLIC_CHANNEL_COMMENTS = "/api/comments/v1/owner/:ownerId/public/:channelId/comments.json";
  static API_COMMENTS_V1_COMMENTS_CREATE = "/api/comments/v1/owner/:ownerId/comments/create.json";
  static API_COMMENTS_V1_COMMENTS_UNAPPROVED_BY_REF = "/api/comments/v1/owner/:ownerId/manage/channels/list-unapproved-by-ref.json";

  static API_COMMENTS_V1_COMMENTS_UNAPPROVED = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/list-unapproved.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_APPROVE = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/approve.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_DELETE = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/delete.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_UNDO_DELETE = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/undo-delete.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_REVISE = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/revise.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_UPDATE_STARRED = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/update-starred.json";
  static API_COMMENTS_V1_MANAGE_CHANNELS_LIST_BY_REF = "/api/comments/v1/owner/:ownerId/manage/channels/list-by-ref.json";
  static API_COMMENTS_V1_MANAGE_CHANNELS_LIST_DELETED_BY_REF = "/api/comments/v1/owner/:ownerId/manage/channels/list-deleted-by-ref.json"
  static API_COMMENTS_V1_MANAGE_CHANNELS_LIST_UNAPPROVED_BY_REF = "/api/comments/v1/owner/:ownerId/manage/channels/list-unapproved-by-ref.json"
  static API_COMMENTS_V1_MANAGE_CHANNELS_SHOW = "/api/comments/v1/owner/:ownerId/manage/channels/:channelId/show.json"
  static API_COMMENTS_V1_MANAGE_COMMENTS_HIGHLIGHT = "/api/comments/v1/owner/:ownerId/manage/comments/:id/update-starred.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_COUNTERS = "/api/comments/v1/owner/:ownerId/manage/comments/counters-by-ref.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_ALL = "/api/comments/v1/owner/:ownerId/manage/comments/list.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_UNAPPROVED = "/api/comments/v1/owner/:ownerId/manage/comments/list-unapproved-by-ref.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_DELETED = "/api/comments/v1/owner/:ownerId/manage/comments/list-deleted-by-ref.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_LABELED = "/api/comments/v1/owner/:ownerId/manage/comments/list-by-label.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_LABELS = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/update-labels.json";
  static API_COMMENTS_V1_MANAGE_COMMENT_DETAIL = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/show.json";
  static API_COMMENTS_V1_MANAGE_COMMENTS_BAN_AUTHOR = "/api/comments/v1/owner/:ownerId/manage/comments/:commentId/ban-author.json";
  static API_COMMENTS_V1_MANAGE_CHANNEL_COMMENTS = "/api/comments/v1/owner/:ownerId/manage/channels/:channelId/show.json";

  static API_COMMENTS_V1_MANAGE_LABELS_ALL = "/api/comments/v1/owner/:ownerId/manage/:refId/labels/list.json";
  static API_COMMENTS_V1_MANAGE_LABELS_ADD = "/api/comments/v1/owner/:ownerId/manage/:refId/labels/create.json";
  static API_COMMENTS_V1_MANAGE_LABELS_UPDATE = "/api/comments/v1/owner/:ownerId/manage/:refId/labels/:labelId/update.json";
  static API_COMMENTS_V1_MANAGE_LABELS_DELETE = "/api/comments/v1/owner/:ownerId/manage/:refId/labels/:labelId/delete.json";

  static API_COMMENTS_V1_MANAGE_BANNED_AUTHORS_LIST = "/api/comments/v1/owner/:ownerId/manage/banned-authors/list.json";
  static API_COMMENTS_V1_MANAGE_BANNED_AUTHORS_UNBAN = "/api/comments/v1/owner/:ownerId/manage/banned-authors/:banId/unban.json";

  static API_COMMENTS_V1_SETTINGS = "/api/comments/v1/owner/:ownerId/settings";
  static API_COMMENTS_V1_SETTINGS_SIGNING_KEYS = "/api/comments/v1/owner/:ownerId/signing-keys";

  static API_EMBED_V4_FULL_PAGE_LIST = "/api/embed/v4/organizations/:organizationId/fullpage/list.json";
  static API_EMBED_V4_FULL_PAGE_CREATE = "/api/embed/v4/organizations/:organizationId/fullpage/create.json";
  static API_EMBED_V4_FULL_PAGE_DELETE = "/api/embed/v4/organizations/:organizationId/fullpage/delete.json";

  static API_EMBED_V4_THEME_LIST = "/api/embed/v4/organizations/:organizationId/themes/list.json";
  static API_EMBED_V4_THEME_CREATE = "/api/embed/v4/organizations/:organizationId/themes/create.json";
  static API_EMBED_V4_THEME_UPDATED = "/api/embed/v4/organizations/:organizationId/themes/:id/update.json";
  static API_EMBED_V4_THEME_DELETE = "/api/embed/v4/organizations/:organizationId/themes/:id/delete.json";
  static API_EMBED_V4_THEME_SHOW = "/api/embed/v4/organizations/:organizationId/themes/:id/show.json";

  static API_EMBED_V4_CUSTOM_STYLE_LIST = "/api/embed/v4/organizations/:organizationId/custom-styles/list.json";
  static API_EMBED_V4_CUSTOM_STYLE_CREATE = "/api/embed/v4/organizations/:organizationId/custom-styles/create.json";
  static API_EMBED_V4_CUSTOM_STYLE_UPDATED = "/api/embed/v4/organizations/:organizationId/custom-styles/:id/update.json";
  static API_EMBED_V4_CUSTOM_STYLE_DELETE = "/api/embed/v4/organizations/:organizationId/custom-styles/:id/delete.json";
  static API_EMBED_V4_CUSTOM_STYLE_SHOW = "/api/embed/v4/organizations/:organizationId/custom-styles/:id/show.json";

  static API_EMBED_V4_LIVEBLOG = "/api/embed/v4/js/liveblog.json"
  static API_EMBED_V4_LIVEBLOG_TEASER = "/api/embed/v4/js/liveblog-teaser.json"

  static API_SHARE_REDIRECT = "/share/event/:eventId"
  static API_V5_SHARING_POST = "/api/v5/sharing/post.json"
  static API_V5_WRITE_API_CLIENTS = "/api/v5/write/api_client/index.json"

  // Customer self-service API-access clients — hosted in gatekeeper (not backend/backend).
  // Same path for GET (list) / POST (create) / DELETE, distinguished by HTTP verb.
  static API_GATEKEEPER_V1_API_ACCESS_CLIENTS = "/api/gatekeeper/v1/api-access-clients"

  static API_V5_WRITE_ORIGIN_PERMIT_INDEX = "/api/v5/write/origin_permit/index.json"
  static API_V5_WRITE_ORIGIN_PERMIT_CREATE = "/api/v5/write/origin_permit/create.json"
  static API_V5_WRITE_ORIGIN_PERMIT_DELETE = "/api/v5/write/origin_permit/delete.json"

  static API_ANALYTICS_V6_STATS_LIVEBLOG_OVERVIEW = "/api/analytics/v6/stats/liveblog/:id/overview.json"
  static API_ANALYTICS_V6_STATS_LIVEBLOG_PERIODS = "/api/analytics/v6/stats/liveblog/:id/:date/periods.json"
  static API_ANALYTICS_V6_STATS_LIVEBLOG_ORIGINS = "/api/analytics/v6/stats/liveblog/:id/:date/origins.json"
  static API_ANALYTICS_V6_STATS_LIVEBLOG_LOCATIONS = "/api/analytics/v6/stats/liveblog/:id/:date/origin/:origin/locations.json"

  static API_ANALYTICS_V7_LIVEBLOG_DASHBOARD = '/api/analytics/v7/liveblog/:id/dashboard/:date.json'
  static API_ANALYTICS_V7_ORGANIZATION_DASHBOARD = '/api/analytics/v7/organization/:id/dashboard/:date.json'


  static API_GATEKEEPER_V1_SESSION_ME = "/api/gatekeeper/v1/session/me.json";

  static API_V6_GUEST_ACCESS_LIST = "/api/v6/guest-access/list-guest-access.json"
  static API_V6_GUEST_ACCESS_CREATE = "/api/v6/guest-access/create-guest-access.json"
  static API_V6_GUEST_ACCESS_UPDATE = "/api/v6/guest-access/update-guest-access.json"
  static API_V6_GUEST_ACCESS_MAKE_LOGIN_LINK = "/api/v6/guest-access/make-login-link.json"
  static API_V6_GUEST_ACCESS_RESEND_INVITE = "/api/v6/guest-access/resend-guest-access-email.json"
  static API_V6_GUEST_ACCESS_DELEGATED_CONTEXTS = "/api/v6/guest-access/delegated-contexts.json"

  static API_EDITOR_V2_COGNITO_AUTH = "/apigw/editor/v3/cognito-auth"
  static API_EDITOR_V2_MEDIA_DOWNLOAD_PROXY = "/apigw/editor/v3/media-download-proxy"
  static API_EDITOR_V2_CONTENT_DOWNLOAD_PROXY = "/apigw/editor/v3/content-download-proxy"
  static API_EDITOR_V2_SUMMARIZE_LIVEBLOG = "/apigw/editor/v3/summarize-liveblog"
  static API_EDITOR_V2_MEDIA_METADATA_EXPORT = "/apigw/editor/v3/media-metadata-export"
  static API_EDITOR_V2_DAM_CUE_PROXY = "/apigw/editor/v3/organization/:organizationId/dam-cue/proxy/:cuePath+"

  static API_SPORTS_V2_DATA_TEAM_SEARCH = "/apigw/sports/v2/:organizationId/data/teamSearch/:dataSource"
  static API_SPORTS_V2_DATA_LEAGUE_SEARCH = "/apigw/sports/v2/:organizationId/data/leagueSearch/:dataSource"
  static API_SPORTS_V2_DATA_PLAYER_SEARCH = "/apigw/sports/v2/:organizationId/data/playerSearch/:dataSource"
  static API_SPORTS_V2_DATA_PLAYER_BY_TEAM = "/apigw/sports/v2/:organizationId/data/playerByTeam/:teamId+"
  static API_SPORTS_V2_DATA_PLAYER_BY_MATCH = "/apigw/sports/v2/:organizationId/data/playerByMatch/:matchId+"
  static API_SPORTS_V2_DATA_MATCH_WITH_EVENTS = "/apigw/sports/v2/:organizationId/data/matchWithEvents/:matchId+"
  static API_SPORTS_V2_DATA_MATCH_DETAILS = "/apigw/sports/v2/:organizationId/data/matchDetails/:matchId+"
  static API_SPORTS_V2_DATA_LEAGUE_MATCHES = "/apigw/sports/v2/:organizationId/data/matchesByRoundAndSeason/:roundId+"
  static API_SPORTS_V2_DATA_LEAGUE_STRUCTURE = "/apigw/sports/v2/:organizationId/data/roundsBySeason/:seasonId+"
  static API_SPORTS_V2_DATA_LIVEBLOG_CREATE = "/apigw/sports/v2/:organizationId/liveblog/createFromExternal"

  static API_V5_WRITE_PLAYER_INDEX = "/api/v5/write/player/index.json"
  static API_V5_WRITE_TEAM_INDEX = "/api/v5/write/team/index.json"
  static API_V5_TAGS_LIST = "/api/v5/tags/list.json"
  static API_CATEGORIES_V1 = "/apigw/categories/v1/organization/:organizationId/categories"
  static API_V5_WRITE_PRICING_INDEX = "/api/v5/write/pricing/index.json"
  static API_V5_WRITE_PRICING_CREATE = "/api/v5/write/pricing/create.json"
  static API_V5_WRITE_PRICING_UPDATE_INACTIVE = "/api/v5/write/pricing/update_inactive.json"
  static API_V5_WRITE_TICKER_SHOW = "/api/v5/write/ticker/show.json"
  static API_V5_WRITE_TICKER_SHOW_BULK = "/api/v5/write/ticker/show_bulk.json"
  static API_V5_WRITE_CUSTOM_TEMPLATE_INDEX = "/api/v5/write/custom_template/index.json"
  static API_V5_WRITE_ORGANIZATION_SHOW = "/api/v5/write/organization/show.json"
  static API_V5_WRITE_ORGANIZATION_UPDATE_MARKETPLACE_INFO = "/api/v5/write/organization/update_marketplace_info.json"
  static API_V5_WRITE_ORGANIZATION_UPDATE = "/api/v5/write/organization/update.json"
  static API_V5_WRITE_ORGANIZATION_UPDATE_GAME_DEFAULTS = "/api/v5/write/organization/update_game_defaults.json"

  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_LIST = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/list`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_CREATE = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/create`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_UPDATE = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/:streamId/update`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_DELETE = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/:streamId/delete`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_MODIFY_AUTO_POST = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/:streamId/modify-auto-post`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_SYNDICATION_TARGETS = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/syndication-targets`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_SYNDICATION_TARGET_DISCONNECT = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/syndication-targets/:targetLiveblogId/disconnect`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_UPDATE_ORDER = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/update-order`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_POLL = `/apigw/social-media/v4/liveblog/:liveblogId/content-streams/:streamId/poll`;
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_LOAD_TICKER_PROXY = "/apigw/social-media/v4/liveblog/:liveblogId/content-streams/:streamId/load-ticker-proxy.json";
  static API_SOCIAL_MEDIA_V4_CONTENT_STREAM_LIST_CUSTOMER_SCRIPTS = "/apigw/social-media/v4/organizations/:organizationId/customer-scripts/list.json";
  static API_SOCIAL_MEDIA_V4_DPA_CONNECTION_LIST = "/apigw/social-media/v4/organizations/:organizationId/dpa-connections/list.json";

  static API_SEARCH_V1_ORGANIZATION_LIVEBLOG = "/apigw/search/v1/organization/:organizationId/liveblogs"

  static API_SETTINGS_V1_ORGANIZATION_ALL = "/apigw/settings/v1/organization/:organizationId/all"
  static API_SETTINGS_V1_ORGANIZATION_UPDATE = "/apigw/settings/v1/organization/:organizationId/:settingsKey/update"

  // Interactive (Quiz) API endpoints
  static API_INTERACTIVE_V1_QUIZ_SUBMIT = "/apigw/interactive/v1/organizations/:organizationId/quizzes/:quizId/submit";

  static API_INTERACTIVE_V1_POLLS_FETCH = "/apigw/interactive/v1/organizations/:organizationId/polls/:pollId";
  static API_INTERACTIVE_V1_POLLS_SUBMIT = "/apigw/interactive/v1/organizations/:organizationId/polls/:pollId/submit";

  static API_INTERACTIVE_V1_REACTIONS_GET = "/apigw/interactive/v1/organizations/:organizationId/liveblogs/:liveblogId/reactions/get.json";
  static API_INTERACTIVE_V1_REACTIONS_CREATE = "/apigw/interactive/v1/organizations/:organizationId/liveblogs/:liveblogId/events/:eventId/reactions/create.json";
  static API_INTERACTIVE_V1_REACTIONS_DELETE = "/apigw/interactive/v1/organizations/:organizationId/liveblogs/:liveblogId/events/:eventId/reactions/delete.json";


  static API_CHANGEBRIDGE_WS = "/changebridge/:liveblogId"

  static API_SUBSCRIPTIONS_V1_DPA_STATE = "/api/subscriptions/v1/dpa_state.json";
  static API_SUBSCRIPTIONS_V1_DPA_CREATE = "/api/subscriptions/v1/dpa/create.json";


  options: ApiClientOptions


  constructor(options: Partial<ApiClientOptions>) {
    this.options = Object.assign({}, defaultOptions, options) as ApiClientOptions;
    if (!this.options.fetchFn) {
      throw new Error("ApiClient requires an injected fetchFn, e.g. (url, init) => enhancedFetch(url, {...init, bot: TickarooBot.<identity>}) from @tickaroo/enhanced-fetch");
    }
  }


  _buildQueryWithAccessToken(query: Record<string, any>, accessToken?: string, isApiGw?: boolean): string {
    if (accessToken) {
      query.access_token = accessToken;
    } else if (this.options.clientId) {
      query.client_id = this.options.clientId;
      if(this.options.clientSecret) {
        query.client_secret = this.options.clientSecret;
      }
    } else {
      throw new FatalApiError("Either access token or client id must be present");
    }

    if(isApiGw) {
      query.access_token = query.access_token || ""
      query.client_id = query.client_id || ""
      query.client_secret = query.client_secret || ""
    }

    return new URLSearchParams(query).toString();
  }

  _parsePathParameters(name: string, query: Record<string, any>) {
    const returnQuery = { ...query };
    const segments = name.split("/")
    const path = segments.map(segment => {
      return segment.split(".").map(subSegment => {
        const offset = subSegment.indexOf(":") + 1
        if (!offset)
          return subSegment

        let greedy = false
        let key = subSegment.slice(offset)
        if(key.endsWith("+")) {
          key = key.slice(0, key.length - 1)
          greedy = true
        }

        const value = returnQuery[key]
        if (value === undefined) {
          throw new Error("Missing PathParam " + key)
        }
        delete returnQuery[key]

        if(!greedy) {
          return encodeURIComponent(value)
        } else {
          return value.split('/').map(encodeURIComponent).join('/')
        }
      }).join(".")
    }).join("/")

    return { path, query: returnQuery }
  }

  _apiUrl(name: string, paramsOrUndefined: any, accessToken?: string) {
    const params = Object.assign({}, paramsOrUndefined);
    const { path, query } = this._parsePathParameters(name, params);
    const isApiGw = path.startsWith("/apigw")
    const hasApiGwHost = !!this.options.apiGwHost
    const queryString = this._buildQueryWithAccessToken(query, accessToken, isApiGw && hasApiGwHost);
    const host = (isApiGw && hasApiGwHost) ? this.options.apiGwHost : this.options.host
    if (queryString) {
      return host + path + "?" + queryString;
    } else {
      return host + path;
    }
  }

  _downloadUrl(path: string, accessToken?: string) {
    const queryString = this._buildQueryWithAccessToken({}, accessToken, false);
    return this.options.host + path + "?" + queryString;
  }

  parseResponse(res: Response) {
    const contentType = res.headers.get("content-type");
    // Check no-content FIRST: a 204 (or empty body) carries no JSON even when the
    // server still sends a application/json content-type, so parsing it would
    // throw "Unexpected end of JSON input". Applies to the tags/list PUT (204).
    if (res.status === 204 || res.headers.get("content-length") === "0") {
      return Promise.resolve(undefined);
    } else if (contentType && contentType.toLowerCase().indexOf("application/json") >= 0) {
      return res.json()
    } else {
      throw new ApiResponseError(0, { message: "Unparsable response, please try again later. Original Status = " + res.status + "" });
    }
  }

  _handleResponse(response: Response) {
    if (this.options.interpretResponse) {
      return this.interpretResponse(response);
    } else {
      return this.parseResponse(response);
    }
  }

  interpretResponse(res: Response) {
    if (res.ok) {
      return this.parseResponse(res);
    } else if (res.status === 401) {
      this.options.invalidateCallback && this.options.invalidateCallback();
      this.throwError("", 401);
    } else if (res.status < 500) {
      return this.parseResponse(res).then(json => {
        this.throwError(json, res.status);
      })
    } else {
      this.throwError(undefined, res.status);
    }
  }

  throwError(json: any, status: number) {
    throw new ApiResponseError(status, json);
  }

  defaultHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Accept-Language": this.options.locale || "en"

    }
    if(this.options.userAgent) {
      headers["User-Agent"] = this.options.userAgent
    }
    if(this.options.gatekeeperDebug) {
      headers["Tik-Gatekeeper-Debug"] = "true"
    }
    return headers

  }

  getJson(name: string, query: any, accessToken?: string) {
    const url = this._apiUrl(name, query, accessToken)
    this.options.log("FETCH", "GET", url)
    return cancelableFetch(this.options.fetchFn, this.options.fetchTimeout, url, {
      method: "GET",
      headers: this.defaultHeaders()
    }).catch(err => {
      throw err
    }).then((resp) => this._handleResponse(resp))
  }

  postJson(name: string, data: any, params: any, accessToken?: string) {
    const url = this._apiUrl(name, params, accessToken)
    this.options.log("FETCH", "POST", url)
    return cancelableFetch(this.options.fetchFn, this.options.fetchTimeout, url, {
      method: "POST",
      headers: {
        "Content-type": "application/json; charset=UTF-8",
        ...this.defaultHeaders()
      },
      body: JSON.stringify(data)
    }).catch(err => {
      throw err
    }).then((resp) => this._handleResponse(resp))
  }

  deleteJson(name: string, data: any, params: any, accessToken?: string) {
    const url = this._apiUrl(name, params, accessToken)
    this.options.log("FETCH", "DELETE", url)
    return cancelableFetch(this.options.fetchFn, this.options.fetchTimeout, url, {
      method: "DELETE",
      headers: {
        "Content-type": "application/json; charset=UTF-8",
        ...this.defaultHeaders()
      },
      body: JSON.stringify(data)
    }).catch(err => {
      throw err
    }).then((resp) => this._handleResponse(resp))
  }

  putJson(name: string, data: any, params: any, accessToken?: string) {
    const url = this._apiUrl(name, params, accessToken)
    this.options.log("FETCH", "PUT", url)
    return cancelableFetch(this.options.fetchFn, this.options.fetchTimeout, url, {
      method: "PUT",
      headers: {
        "Content-type": "application/json; charset=UTF-8",
        ...this.defaultHeaders()
      },
      body: JSON.stringify(data)
    }).catch(err => {
      throw err
    }).then((resp) => this._handleResponse(resp))
  }

}

export class AuthenticatedApiClient {
  private readonly apiClient: ApiClient;
  readonly accessToken: () => Promise<string|undefined>;

  constructor(apiClient: ApiClient, accessToken?: () => Promise<string|undefined>) {
    this.apiClient = apiClient;
    this.accessToken = accessToken || (() => Promise.resolve(undefined));
  }

  // Resolves the (async) access token and then delegates to `fn`, while keeping
  // the returned promise cancelable: cancelling aborts the in-flight request and
  // skips starting one if the token hasn't resolved yet.
  private _withAccessToken<T>(fn: (accessToken: string|undefined) => CancelablePromiseType<T>): CancelablePromiseType<T> {
    return new CancelablePromise<T>((resolve, reject, onCancel) => {
      let inner: CancelablePromiseType<T> | undefined;
      let canceled = false;
      onCancel(() => {
        canceled = true;
        inner?.cancel();
      });
      this.accessToken().then(accessToken => {
        if (canceled) {
          return;
        }
        inner = fn(accessToken);
        inner.then(resolve, reject);
      }, reject);
    });
  }

  async isAuthenticated() {
    return !!(await this.accessToken());
  }

  getJson(name: string, query: any = {}) {
    return this._withAccessToken(accessToken => this.apiClient.getJson(name, query, accessToken));
  }

  get<T>(name: string, query: any = {}): CancelablePromiseType<T> {
    return this._withAccessToken<T>(accessToken => this.apiClient.getJson(name, query, accessToken) as CancelablePromiseType<T>);
  }

  async buildDownloadUrl(name: string) {
    return this.apiClient._downloadUrl(name, await this.accessToken())
  }

  postJson(name: string, data: any, params?: any) {
    return this._withAccessToken(accessToken => this.apiClient.postJson(name, data, params, accessToken));
  }

  delete<T>(name: string, data: any, params?: any): CancelablePromiseType<T> {
    return this._withAccessToken<T>(accessToken => this.apiClient.deleteJson(name, data, params, accessToken) as CancelablePromiseType<T>);
  }

  deleteJson(name: string, data: any, params?: any) {
    return this._withAccessToken(accessToken => this.apiClient.deleteJson(name, data, params, accessToken));
  }

  post<T>(name: string, data: any, params?: any): CancelablePromiseType<T> {
    return this._withAccessToken<T>(accessToken => this.apiClient.postJson(name, data, params, accessToken) as CancelablePromiseType<T>);
  }

  putJson(name: string, data: any, params?: any) {
    return this._withAccessToken(accessToken => this.apiClient.putJson(name, data, params, accessToken));
  }

  put<T>(name: string, data: any, params?: any): CancelablePromiseType<T> {
    return this._withAccessToken<T>(accessToken => this.apiClient.putJson(name, data, params, accessToken) as CancelablePromiseType<T>);
  }

  async buildUrl(name: string, query: any = {}) {
    return this.apiClient._apiUrl(name, query, await this.accessToken())
  }
}
